import React from 'react'
import { useQuery } from 'react-query'
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Button
} from '@mui/material'
import {
  Description,
  Business,
  Settings,
  People,
  Visibility,
  Edit,
  Add
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../common/LoadingSpinner'
import axios from 'axios'

const Dashboard = () => {
  const { user, hasPermission } = useAuth()
  const navigate = useNavigate()

  const { data: stats, isLoading: statsLoading } = useQuery(
    'dashboard-stats',
    async () => {
      const token = localStorage.getItem('token')
      const [gestionesResp, procesosResp] = await Promise.all([
        axios.get('/api/gestiones', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/procesos', { headers: { Authorization: `Bearer ${token}` } })
      ])
      return { 
        gestiones: gestionesResp.data,
        procesos: procesosResp.data
      }
    },
    {
      staleTime: 5 * 60 * 1000,
    }
  )

  const { data: recentProcedures, isLoading: proceduresLoading } = useQuery(
    'recent-procedures',
    async () => {
      const response = await axios.get('/api/procedimientos?limit=5&sort=updated_at')
      return response.data?.procedimientos || []
    },
    {
      staleTime: 2 * 60 * 1000,
    }
  )

  if (statsLoading) {
    return <LoadingSpinner message="Cargando dashboard..." />
  }

  const getWelcomeMessage = () => {
    const hour = new Date().getHours()
    if (hour < 12) return '¡Buenos días!'
    if (hour < 18) return '¡Buenas tardes!'
    return '¡Buenas noches!'
  }

  const getRoleDescription = (role) => {
    switch (role) {
      case 'administrador':
        return 'Tienes acceso completo al sistema'
      case 'editor':
        return `Puedes gestionar procedimientos de ${user.gestion?.nombre || 'tu gestión'}`
      case 'consultor':
        return 'Puedes consultar y exportar todos los procedimientos'
      default:
        return ''
    }
  }

  const totalProcedimientos = stats?.gestiones?.reduce((total, gestion) => 
    total + (gestion.total_procedimientos || 0), 0
  ) || 0

  const totalAprobados = stats?.gestiones?.reduce((total, gestion) => 
    total + (gestion.procedimientos_aprobados || 0), 0
  ) || 0

  return (
    <Box>
      {/* Header de bienvenida */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom>
              {getWelcomeMessage()} {user?.nombre}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {getRoleDescription(user?.rol)}
            </Typography>
            {user?.gestion && (
              <Chip
                label={`Gestión ${user.gestion.codigo}`}
                sx={{
                  mt: 1,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white'
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            {hasPermission(['administrador', 'editor']) && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Add />}
                onClick={() => navigate('/procedimientos/nuevo')}
                sx={{ mr: 1 }}
              >
                Nuevo Procedimiento
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => navigate('/procedimientos')}
              sx={{ 
                borderColor: 'rgba(255,255,255,0.5)', 
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Ver Todos
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tarjetas de estadísticas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Business sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Gestiones
                  </Typography>
                  <Typography variant="h4">
                    {stats?.gestiones?.length || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Description sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Procedimientos
                  </Typography>
                  <Typography variant="h4">
                    {totalProcedimientos}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Settings sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Procesos
                  </Typography>
                  <Typography variant="h4">
                    {stats?.procesos?.length || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <People sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Mi Rol
                  </Typography>
                  <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                    {user?.rol}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Gestiones disponibles */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Gestiones del Sistema
              </Typography>
              <List>
                {stats?.gestiones?.map((gestion) => (
                  <ListItem
                    key={gestion.id}
                    divider
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                    onClick={() => navigate(`/gestiones/${gestion.id}`)}
                    secondaryAction={
                      <Box>
                        <Chip
                          label={`${gestion.total_procedimientos || 0} proc.`}
                          size="small"
                          sx={{
                            backgroundColor: `${gestion.color || '#64B5F6'}20`,
                            color: gestion.color || '#64B5F6',
                            border: `1px solid ${gestion.color || '#64B5F6'}`,
                            fontWeight: 'bold'
                          }}
                        />
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/gestiones/${gestion.id}`)
                          }}
                          sx={{ ml: 1 }}
                        >
                          <Visibility />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: gestion.color || '#64B5F6'
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={gestion.codigo}
                            size="small"
                            sx={{
                              backgroundColor: gestion.color || '#64B5F6',
                              color: '#fff',
                              fontWeight: 'bold',
                              fontSize: '0.75rem'
                            }}
                          />
                          <Typography variant="body1">{gestion.nombre}</Typography>
                        </Box>
                      }
                      secondary={`${gestion.procedimientos_aprobados || 0} aprobados de ${gestion.total_procedimientos || 0} totales`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Procedimientos recientes */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actividad Reciente
              </Typography>
              {proceduresLoading ? (
                <LoadingSpinner size={24} message="Cargando..." />
              ) : (
                <List>
                  {recentProcedures?.length > 0 ? (
                    recentProcedures.map((procedimiento) => (
                      <ListItem
                        key={procedimiento.id}
                        divider
                        secondaryAction={
                          <Box>
                            <Chip
                              label={procedimiento.estado}
                              size="small"
                              color={
                                procedimiento.estado === 'aprobado' ? 'success' :
                                procedimiento.estado === 'revision' ? 'warning' : 'default'
                              }
                            />
                            <IconButton
                              edge="end"
                              onClick={() => navigate(`/procedimientos/${procedimiento.id}`)}
                              sx={{ ml: 1 }}
                            >
                              {hasPermission(['administrador', 'editor']) ? <Edit /> : <Visibility />}
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemIcon>
                          <Description color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={procedimiento.titulo}
                          secondary={`${procedimiento.codigo} • ${procedimiento.gestion_nombre}`}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                      No hay procedimientos recientes
                    </Typography>
                  )}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Footer informativo */}
      <Paper sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Sistema de Gestión de Calidad • Escuela Normal Superior del Alto Sinú • 
          Versión 1.0.0
        </Typography>
      </Paper>
    </Box>
  )
}

export default Dashboard